import { createSlice, Reducer } from '@reduxjs/toolkit';
import { cloneDeep } from 'lodash-es';
import { resetState } from 'store/actions';
import { storeName, pdpUserInitialState } from './config';
import {
  updateList,
  setFullData,
  changeFilter,
  getPDP,
  createPDP,
  updatePDP,
  deletePDP,
  getUserPDPs,
  getPDPComments,
  createPDPComment,
  pdpStateTransition,
  updateActionStepCompletion,
} from '../actions';
import { updateActionStepCompletionInState } from '../utils/updateActionStepCompletion';

export * from './interfaces';

const pdp = createSlice({
  name: storeName,
  initialState: cloneDeep(pdpUserInitialState),
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(resetState, (state) => {
      const newState = cloneDeep(pdpUserInitialState);
      newState.filters = state.filters;
      Object.assign(state, newState);
    });

    builder.addCase(updateList.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(updateList.fulfilled, (state, action) => {
      state.loading = false;
      state.items = action.payload;
      state.init = true;
    });

    builder.addCase(getUserPDPs.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(getUserPDPs.fulfilled, (state, action) => {
      state.loading = false;
      state.items = action.payload;
      state.init = true;
    });

    builder.addCase(getUserPDPs.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(updateList.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(setFullData, (state, action) => {
      state.fullData[action.payload.id] = action.payload;
    });

    builder.addCase(changeFilter, (state, action) => {
      if (action.payload.store === storeName) {
        (state.filters as any)[action.payload.filter] = action.payload.value;
      }
    });

    builder.addCase(getPDP.fulfilled, (state, action) => {
      state.fullData[action.payload.id] = action.payload;
    });

    builder.addCase(createPDP.fulfilled, (state, action) => {
      state.items.unshift(action.payload);
      state.fullData[action.payload.id] = action.payload;
    });

    builder.addCase(updatePDP.fulfilled, (state, action) => {
      const index = state.items.findIndex(
        (item) => item.id === action.payload.id,
      );
      if (index !== -1) {
        state.items[index] = action.payload;
      }
      state.fullData[action.payload.id] = action.payload;
    });

    builder.addCase(deletePDP.fulfilled, (state, action) => {
      state.items = state.items.filter(
        (item) => item.id !== action.meta.arg.id,
      );
      delete state.fullData[action.meta.arg.id];
    });

    builder.addCase(pdpStateTransition.fulfilled, (state, action) => {
      const index = state.items.findIndex(
        (item) => item.id === action.payload.id,
      );
      if (index !== -1) {
        state.items[index] = action.payload;
      }
      state.fullData[action.payload.id] = action.payload;
    });

    // Optimistic update - apply immediately when request starts
    builder.addCase(updateActionStepCompletion.pending, (state, action) => {
      updateActionStepCompletionInState(state, action);
    });

    // Revert optimistic update on error (use opposite of requested value)
    builder.addCase(updateActionStepCompletion.rejected, (state, action) => {
      updateActionStepCompletionInState(
        state,
        action,
        !action.meta.arg.isAchieved,
      );
    });

    builder.addCase(getPDPComments.fulfilled, (state, action) => {
      const { threadId } = action.meta.arg;
      state.comments[threadId] = action.payload;
    });

    builder.addCase(createPDPComment.fulfilled, (state, action) => {
      const { threadId } = action.meta.arg;
      if (!state.comments[threadId]) {
        state.comments[threadId] = [];
      }
      state.comments[threadId].push(action.payload);
    });
  },
});

export default pdp.reducer as Reducer<typeof pdpUserInitialState>;
